import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardActionArea,
  CardContent,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';

import { TP_COLORS } from '../../../../constants';
import { IQuiz } from '../../../../store/QuizSlice';
import {
  getSubjectColor,
  getQuestionDifficultyChipColor,
} from '../../../../helper';
import SubjectCodeCard from '../../../../components/Card/SubjectCodeCard';

export interface IQuizCard {
  quiz: IQuiz;
}

const QuizCard: React.FC<IQuizCard> = ({ quiz }) => {
  const navigate = useNavigate();

  let subjectCode = '';
  let subject = '';
  let curriculum = '';
  let topicName = '';
  let quizSubTopics: IQuiz['subTopics'] = [];

  if (quiz && quiz.topic) {
    const topicData = quiz.topic;

    subjectCode = topicData.subjectCode || '';
    subject = topicData.subject || '';
    curriculum = topicData.curriculum || '';
    topicName = topicData.name || '';
  }

  if (quiz && quiz.subTopics && quiz.subTopics.length) {
    quizSubTopics = quiz.subTopics || [];
  }

  const cardHeader = (
    <Box
      sx={{
        background: `url('${
          process.env.PUBLIC_URL
        }/images/shapes/learning-gadgets.transparent.png') top right/contain no-repeat, ${getSubjectColor(
          subject
        )}`,
        borderRadius: '7px',
        padding: '10px',
        mb: '10px',
      }}
    >
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: '3px' }}>
        {quiz.assessment ? (
          <SubjectCodeCard
            size="small"
            label="Assessment"
            style={{ marginRight: '5px' }}
          />
        ) : null}
        <SubjectCodeCard size="small" label={subjectCode} />
      </Stack>
      <Typography
        fontWeight={600}
        sx={{ color: TP_COLORS.white }}
        className="ellipsis-text"
      >
        {subject}
      </Typography>
      <Typography
        fontWeight={300}
        className="ellipsis-text"
        sx={{ color: TP_COLORS.white, opacity: 0.5 }}
      >
        {curriculum}
      </Typography>
    </Box>
  );

  const cardBody = (
    <div style={{ padding: '0 5px' }}>
      <Typography
        fontWeight={600}
        variant="subtitle2"
        className="ellipsis-text"
        sx={{ mt: '20px' }}
      >
        {topicName}
      </Typography>
      <div style={{ marginBottom: '20px' }}>
        {quizSubTopics.map((quizSubTopic, index) => {
          const subTopicText = `${quizSubTopic.subTopic?.name || ''} - ${
            quizSubTopic.paper
          }`;
          return (
            <Typography
              key={index}
              variant="subtitle2"
              color="secondary"
              className="ellipsis-text"
              title={subTopicText}
            >
              {subTopicText}
            </Typography>
          );
        })}
      </div>
    </div>
  );
  const cardFooter = (
    <Stack direction="row" alignItems="center">
      <Typography
        variant="subtitle2"
        color="secondary"
        sx={{ mr: '10px', display: 'flex', alignItems: 'center' }}
      >
        <ForumIcon
          fontSize="small"
          sx={{ mr: '5px', color: 'secondary.light' }}
        />
        {quiz.noOfQuestions} Question{quiz.noOfQuestions > 1 ? 's' : ''}
      </Typography>
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{
          color: `${getQuestionDifficultyChipColor(quiz.difficulty)}.main`,
        }}
      >
        {quiz.difficulty}
      </Typography>
    </Stack>
  );
  return (
    <Card style={{ height: '100%' }}>
      <CardActionArea
        onClick={() =>
          navigate(
            `${process.env.PUBLIC_URL}/dashboard/quiz-bank/questions/${quiz._id}`
          )
        }
        style={{ height: '100%' }}
      >
        <CardContent sx={{ p: '10px', height: '100%' }}>
          {cardHeader}
          <div style={{ padding: '0 10px' }}>
            {cardBody}
            {cardFooter}
          </div>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default QuizCard;
